import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Trophy,
  Users,
  Calendar,
  Activity,
  Plus,
  ArrowRight,
  TrendingUp,
  Eye,
  AlertTriangle,
  Radio,
  ShieldCheck,
  Clock3,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';

const formatBallEvent = (ball) => {
  if (ball.is_wicket) return 'W';
  if (ball.is_wide) return ball.runs > 0 ? `Wd${ball.runs}` : 'Wd';
  if (ball.is_no_ball) return ball.runs > 0 ? `Nb${ball.runs}` : 'Nb';
  return `${ball.runs}`;
};

const getBallChipClass = (ball) => {
  if (ball.is_wicket) return 'bg-red-500/15 text-red-300 border-red-500/30';
  if (ball.is_wide) return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (ball.is_no_ball) return 'bg-orange-500/15 text-orange-200 border-orange-500/30';
  if (ball.runs >= 4) return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30';
  if (ball.runs === 0) return 'bg-white/5 text-white/55 border-white/10';
  return 'bg-primary/10 text-primary border-primary/20';
};

const SectionHeading = ({ eyebrow, title, body, action }) => (
  <div className="mb-3 flex flex-col gap-2.5 sm:mb-4 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
    <div>
      {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">{eyebrow}</p>}
      <h2 className="mt-1 text-lg font-semibold text-white sm:text-xl">{title}</h2>
      {body && <p className="mt-1 max-w-2xl text-[13px] leading-5 text-white/60 sm:text-sm sm:leading-6">{body}</p>}
    </div>
    {action}
  </div>
);

const DashboardLoading = () => (
  <Layout title="Dashboard">
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="h-36 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        ))}
      </div>
      <div className="grid gap-6 xl:grid-cols-[1.35fr_0.85fr]">
        <div className="h-[420px] animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        <div className="h-[420px] animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="h-[320px] animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        <div className="h-[320px] animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
      </div>
    </div>
  </Layout>
);

const LiveMatchInsights = ({ match }) => {
  const summary = match.live_summary;

  if (!summary) return null;

  return (
    <div className="mt-3 space-y-1 text-[13px] leading-5 sm:mt-4 sm:space-y-1.5 sm:text-sm">
      {summary.striker && (
        <p className="text-white">
          <span className="font-medium text-white/70">On strike:</span> {summary.striker.name}{' '}
          <span className="text-amber-300">★</span>
          <span className="text-white/50"> {summary.striker.runs} ({summary.striker.balls})</span>
        </p>
      )}
      {summary.non_striker && (
        <p className="text-white/60">
          <span className="font-medium text-white/75">Non-striker:</span> {summary.non_striker.name}
          <span> {summary.non_striker.runs} ({summary.non_striker.balls})</span>
        </p>
      )}
      {summary.bowler && (
        <p className="text-white/60">
          <span className="font-medium text-white/75">Bowler:</span> {summary.bowler.name}
          <span> {summary.bowler.overs}.{summary.bowler.balls}-{summary.bowler.runs}-{summary.bowler.wickets}</span>
        </p>
      )}
      {summary.target && (
        <p className="text-white/60">
          <span className="font-medium text-white/75">Target:</span> {summary.target}
          <span> • Need {summary.remaining_runs} more</span>
        </p>
      )}
    </div>
  );
};

const ThisOver = ({ innings }) => {
  const currentOver = innings?.current_over || [];
  if (currentOver.length === 0) return null;

  return (
    <div className="mt-4">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">This Over</p>
      <div className="flex flex-wrap gap-1.5 sm:gap-2">
        {currentOver.map((ball) => (
          <div
            key={ball.id}
            className={`flex h-8 min-w-8 items-center justify-center rounded-full border px-2 text-xs font-semibold sm:h-10 sm:min-w-10 sm:text-sm ${getBallChipClass(ball)}`}
          >
            {formatBallEvent(ball)}
          </div>
        ))}
      </div>
    </div>
  );
};

const LiveMatchCard = ({ match, to, accent = 'viewer' }) => {
  const currentInnings = match.innings?.[match.innings.length - 1];

  return (
    <Link
      to={to}
      className={`block rounded-[24px] border p-4 transition-all duration-200 hover:-translate-y-0.5 sm:rounded-3xl sm:p-5 ${
        accent === 'admin'
          ? 'border-primary/20 bg-primary/[0.08] hover:bg-primary/[0.12]'
          : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.07]'
      }`}
    >
      <div className="mb-3 flex items-center justify-between gap-3 sm:mb-4">
        <Badge variant="destructive" className="gap-1 rounded-full px-2.5 py-1 text-[11px] pulse-live sm:px-3">
          <Radio className="h-3 w-3" />
          LIVE
        </Badge>
        <span className="text-xs text-white/45">{match.round_name}</span>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold leading-6 text-white sm:text-base">{match.team1?.name} vs {match.team2?.name}</p>
          {currentInnings && (
            <>
              <p className="mt-2 text-[34px] font-bold tracking-tight text-white score-display sm:mt-3 sm:text-3xl">
                {currentInnings.runs}/{currentInnings.wickets}
                <span className="ml-1.5 text-xs font-medium text-white/45 sm:ml-2 sm:text-sm">
                  ({currentInnings.overs}.{currentInnings.balls})
                </span>
              </p>
              <LiveMatchInsights match={match} />
              <ThisOver innings={currentInnings} />
            </>
          )}
        </div>
        <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-white/35 sm:h-5 sm:w-5" />
      </div>
    </Link>
  );
};

const MetricCard = ({ title, value, subtitle, icon: Icon, tone = 'primary' }) => {
  const tones = {
    primary: 'from-primary/20 via-primary/10 to-transparent text-primary',
    blue: 'from-sky-500/20 via-sky-500/10 to-transparent text-sky-300',
    violet: 'from-violet-500/20 via-violet-500/10 to-transparent text-violet-300',
    red: 'from-red-500/20 via-red-500/10 to-transparent text-red-300',
  };

  return (
    <Card className="overflow-hidden rounded-[24px] border-white/10 bg-white/[0.05] sm:rounded-3xl">
      <CardContent className="relative p-4 sm:p-5">
        <div className={`absolute inset-x-0 top-0 h-24 bg-gradient-to-br ${tones[tone]}`} />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">{title}</p>
            <p className="mt-2 text-3xl font-bold tracking-tight text-white score-display sm:mt-3 sm:text-4xl">{value}</p>
            {subtitle && <p className="mt-1.5 text-[13px] leading-5 text-white/55 sm:mt-2 sm:text-sm">{subtitle}</p>}
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/25 p-2.5 sm:p-3">
            <Icon className="h-4 w-4 text-white sm:h-5 sm:w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RecentMatchesCard = ({ matches }) => (
  <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg text-white">
        <Calendar className="h-5 w-5 text-primary" />
        Recent Matches
      </CardTitle>
    </CardHeader>
    <CardContent>
      {matches.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 px-4 py-10 text-center text-white/45">
          No completed matches yet
        </div>
      ) : (
        <div className="space-y-3">
          {matches.map((match) => {
            const firstInnings = match.innings?.[0];
            const secondInnings = match.innings?.[1];

            return (
              <Link
                key={match.id}
                to={`/live/${match.id}`}
                className="block rounded-2xl border border-white/10 bg-black/20 p-3.5 transition-colors hover:bg-white/[0.05] sm:p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[15px] font-semibold leading-6 text-white sm:text-base">{match.team1?.name} vs {match.team2?.name}</p>
                    <p className="text-xs text-white/40">{match.round_name}</p>
                  </div>
                  <Badge variant="secondary">Completed</Badge>
                </div>
                {(firstInnings || secondInnings) && (
                  <div className="mt-2.5 space-y-1 text-[13px] leading-5 text-white/55 sm:mt-3 sm:text-sm">
                    {firstInnings && (
                      <p>
                        <span className="font-medium text-white/80">{firstInnings.batting_team?.name}:</span>{' '}
                        {firstInnings.runs}/{firstInnings.wickets} ({firstInnings.overs}.{firstInnings.balls})
                      </p>
                    )}
                    {secondInnings && (
                      <p>
                        <span className="font-medium text-white/80">{secondInnings.batting_team?.name}:</span>{' '}
                        {secondInnings.runs}/{secondInnings.wickets} ({secondInnings.overs}.{secondInnings.balls})
                      </p>
                    )}
                  </div>
                )}
                {match.result && <p className="mt-3 text-sm font-medium text-primary">{match.result}</p>}
              </Link>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

const AlertPanel = ({ alerts }) => (
  <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg text-white">
        <AlertTriangle className="h-5 w-5 text-red-300" />
        Conflict Alerts
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {alerts.length === 0 ? (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-sm text-emerald-200">
          No active conflicts detected. Operations look clear right now.
        </div>
      ) : (
        alerts.map((alert, index) => (
          <div key={`${alert.title}-${index}`} className="rounded-2xl border border-red-500/20 bg-red-500/10 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-red-100">{alert.title}</p>
                <p className="mt-1 text-sm text-red-100/75">{alert.body}</p>
              </div>
              <Badge className="bg-red-500/15 text-red-100">{alert.tone}</Badge>
            </div>
          </div>
        ))
      )}
    </CardContent>
  </Card>
);

const AdminQuickActions = ({ tournaments = [] }) => (
  <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg text-white">
        <TrendingUp className="h-5 w-5 text-primary" />
        Quick Actions
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <Link to="/tournaments">
        <Button className="w-full justify-between rounded-2xl" data-testid="create-tournament-btn">
          <span className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Open Tournament Workspace
          </span>
          <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>

      <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Recent Tournaments</p>
        <div className="mt-3 space-y-2">
          {tournaments.length === 0 ? (
            <p className="text-sm text-white/45">Create your first tournament to unlock the full control center.</p>
          ) : (
            tournaments.slice(0, 4).map((tournament) => (
              <Link
                key={tournament.id}
                to={`/tournaments/${tournament.id}`}
                className="flex items-center justify-between rounded-2xl px-3 py-2.5 transition-colors hover:bg-white/[0.05] sm:py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium text-white sm:text-sm">{tournament.name}</p>
                  <p className="text-xs text-white/40">{tournament.status}</p>
                </div>
                <ChevronStat tournament={tournament} />
              </Link>
            ))
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const ChevronStat = ({ tournament }) => (
  <Badge variant={tournament.status === 'live' ? 'default' : 'secondary'}>
    {tournament.status}
  </Badge>
);

const TournamentGrid = ({ tournaments }) => (
  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 sm:gap-4">
    {tournaments.map((tournament) => (
      <Link
        key={tournament.id}
        to={`/tournaments/${tournament.id}`}
        className="group rounded-[24px] border border-white/10 bg-white/[0.04] p-4 transition-all hover:-translate-y-0.5 hover:bg-white/[0.07] sm:rounded-3xl sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="rounded-2xl border border-primary/20 bg-primary/10 p-2.5 text-primary sm:p-3">
            <Trophy className="h-4 w-4 sm:h-5 sm:w-5" />
          </div>
          <Badge variant={tournament.status === 'live' ? 'default' : 'secondary'}>
            {tournament.status}
          </Badge>
        </div>
        <h3 className="mt-3 text-base font-semibold leading-6 text-white sm:mt-4 sm:text-lg">{tournament.name}</h3>
        <p className="mt-1 text-[13px] leading-5 text-white/50 sm:text-sm">
          {tournament.format} • {tournament.overs_per_match} overs
        </p>
        <div className="mt-4 flex items-center justify-between text-[12px] text-white/55 sm:mt-5 sm:text-sm">
          <span>{tournament.start_date}</span>
          <span className="inline-flex items-center gap-1 text-primary">
            Explore
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </Link>
    ))}
  </div>
);

const Dashboard = () => {
  const { getAuthHeader, isOrganizer, user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [liveMatches, setLiveMatches] = useState([]);
  const [recentMatches, setRecentMatches] = useState([]);
  const [publicTournaments, setPublicTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  const dashboardTab = new URLSearchParams(location.search).get('tab');

  const fetchData = useCallback(async (showError = true) => {
    try {
      if (isOrganizer()) {
        const [statsRes, liveRes, recentMatchesRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, getAuthHeader()),
          axios.get(`${API}/matches/live/all`, getAuthHeader()),
          axios.get(`${API}/matches/completed/recent?limit=5`),
        ]);
        setStats(statsRes.data);
        setLiveMatches(liveRes.data);
        setRecentMatches(recentMatchesRes.data);
      } else {
        const [tournamentsRes, liveRes, recentMatchesRes] = await Promise.all([
          axios.get(`${API}/tournaments/public`),
          axios.get(`${API}/matches/live/all`),
          axios.get(`${API}/matches/completed/recent?limit=5`),
        ]);
        setPublicTournaments(tournamentsRes.data);
        setLiveMatches(liveRes.data);
        setRecentMatches(recentMatchesRes.data);
      }
    } catch (error) {
      if (showError) toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [isOrganizer, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  if (loading) {
    return <DashboardLoading />;
  }

  if (!isOrganizer()) {
    const featuredLiveMatch = liveMatches[0];

    return (
      <Layout title="Dashboard">
        <div className="space-y-6">
          <Card className="overflow-hidden rounded-[24px] border-primary/20 bg-[linear-gradient(135deg,rgba(170,255,0,0.16),rgba(255,255,255,0.03)_55%,rgba(255,255,255,0.02))] sm:rounded-[28px]">
            <CardContent className="p-4 sm:p-8">
              <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] font-medium text-white/65 sm:px-3 sm:text-xs">
                    <Eye className="h-3.5 w-3.5 text-primary" />
                    Viewer Hub
                  </div>
                  <h2 className="mt-3 text-[30px] font-bold leading-[1.05] tracking-tight text-white sm:mt-4 sm:text-4xl">
                    Welcome back, {user?.name}
                  </h2>
                  <p className="mt-2.5 max-w-xl text-[13px] leading-5 text-white/68 sm:mt-3 sm:text-sm sm:leading-6">
                    Stay close to the action with live scores, ongoing tournaments, and recent results in one personalized hub.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2 sm:mt-6 sm:gap-3">
                    <Link to="/tournaments">
                      <Button className="h-10 rounded-full px-4 text-[13px] sm:h-11 sm:px-5 sm:text-sm">
                        Explore Tournaments
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to={featuredLiveMatch ? `/live/${featuredLiveMatch.id}` : '/tournaments'}>
                      <Button variant="outline" className="h-10 rounded-full border-white/10 bg-black/20 px-4 text-[13px] text-white hover:bg-white/[0.06] sm:h-11 sm:text-sm">
                        {featuredLiveMatch ? 'Jump to Live Match' : 'Browse Match Center'}
                      </Button>
                    </Link>
                  </div>

                  {featuredLiveMatch && (
                    <Link
                      to={`/live/${featuredLiveMatch.id}`}
                      className="mt-4 block rounded-[18px] border border-white/10 bg-black/20 p-3 md:hidden"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-[14px] font-semibold text-white">
                            {featuredLiveMatch.team1?.name} vs {featuredLiveMatch.team2?.name}
                          </p>
                          {featuredLiveMatch.innings?.length > 0 && (
                            <p className="mt-1 text-[12px] text-white/55">
                              {featuredLiveMatch.innings[featuredLiveMatch.innings.length - 1]?.runs}/
                              {featuredLiveMatch.innings[featuredLiveMatch.innings.length - 1]?.wickets}{' '}
                              ({featuredLiveMatch.innings[featuredLiveMatch.innings.length - 1]?.overs}.
                              {featuredLiveMatch.innings[featuredLiveMatch.innings.length - 1]?.balls})
                            </p>
                          )}
                        </div>
                        <Badge variant="destructive" className="rounded-full px-2.5 py-1 text-[10px]">
                          LIVE
                        </Badge>
                      </div>
                    </Link>
                  )}
                </div>

                <div className="hidden rounded-[22px] border border-white/10 bg-black/25 p-3.5 md:block sm:rounded-[28px] sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">
                    {featuredLiveMatch ? 'Featured Live Match' : 'Tournament Momentum'}
                  </p>
                  {featuredLiveMatch ? (
                    <div className="mt-3 sm:mt-4">
                      <LiveMatchCard match={featuredLiveMatch} to={`/live/${featuredLiveMatch.id}`} />
                    </div>
                  ) : (
                    <div className="mt-5 rounded-3xl border border-dashed border-white/10 px-5 py-10 text-center text-white/45">
                      No live matches right now. Recent results and active tournaments are ready below.
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
            <div className="space-y-6">
              <div id="live-section">
                <SectionHeading
                  eyebrow="Live Now"
                  title="My Favorites Style Live Feed"
                  body="A fast-moving stream of live fixtures, recent over updates, and on-strike context."
                />
                <div className="grid gap-4">
                  {liveMatches.length === 0 ? (
                    <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
                      <CardContent className="px-6 py-12 text-center text-white/45">
                        No live matches at the moment
                      </CardContent>
                    </Card>
                  ) : (
                    liveMatches.map((match) => (
                      <LiveMatchCard key={match.id} match={match} to={`/live/${match.id}`} />
                    ))
                  )}
                </div>
              </div>

              <div>
                <SectionHeading
                  eyebrow="Ongoing Tournaments"
                  title="Tournament Hub"
                  body="Jump into standings, schedules, public match centers, and announcements."
                />
                {publicTournaments.length === 0 ? (
                  <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
                    <CardContent className="px-6 py-12 text-center text-white/45">
                      No tournaments available yet
                    </CardContent>
                  </Card>
                ) : (
                  <TournamentGrid tournaments={publicTournaments} />
                )}
              </div>
            </div>

            <RecentMatchesCard matches={recentMatches} />
          </div>
        </div>
      </Layout>
    );
  }

  const alerts = [];
  if ((stats?.total_tournaments || 0) === 0) {
    alerts.push({
      title: 'No active tournaments configured',
      body: 'Create your first tournament to activate registrations, teams, and fixtures.',
      tone: 'Action',
    });
  }
  if ((stats?.live_matches || 0) >= 3) {
    alerts.push({
      title: 'Heavy live-operations load',
      body: 'Multiple matches are currently live. Keep officials aligned and watch for scoring delays.',
      tone: 'Watch',
    });
  }
  if ((stats?.total_matches || 0) > 0 && liveMatches.length === 0) {
    alerts.push({
      title: 'No live matches in progress',
      body: 'All fixtures are either scheduled or completed. This is a good window for schedule adjustments.',
      tone: 'Info',
    });
  }

  const controlMetrics = [
    {
      title: 'Tournaments',
      value: stats?.total_tournaments || 0,
      subtitle: 'Active operational competitions',
      icon: Trophy,
      tone: 'primary',
    },
    {
      title: 'Teams',
      value: stats?.total_teams || 0,
      subtitle: 'Registered squads in the system',
      icon: Users,
      tone: 'blue',
    },
    {
      title: 'Matches',
      value: stats?.total_matches || 0,
      subtitle: 'Scheduled and completed fixtures',
      icon: Calendar,
      tone: 'violet',
    },
    {
      title: 'Live Now',
      value: stats?.live_matches || 0,
      subtitle: 'Real-time operational workload',
      icon: Activity,
      tone: 'red',
    },
  ];

  const liveFirst = dashboardTab === 'live';

  return (
    <Layout title="Dashboard">
      <div className="space-y-6">
        <Card className="overflow-hidden rounded-[24px] border-primary/20 bg-[linear-gradient(130deg,rgba(170,255,0,0.16),rgba(255,255,255,0.03)_52%,rgba(255,255,255,0.01))] sm:rounded-[28px]">
          <CardContent className="p-4 sm:p-8">
            <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-2.5 py-1 text-[11px] font-medium text-white/65 sm:px-3 sm:text-xs">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                  Organizer Control Center
                </div>
                <h2 className="mt-3 text-[30px] font-bold leading-[1.05] tracking-tight text-white sm:mt-4 sm:text-4xl">
                  Run tournaments with live operational clarity
                </h2>
                <p className="mt-2.5 max-w-2xl text-[13px] leading-5 text-white/68 sm:mt-3 sm:text-sm sm:leading-6">
                  Monitor live fixtures, recent tournaments, and operational friction from a single overview grid optimized for quick decisions.
                </p>
                <div className="mt-4 flex flex-wrap gap-2.5 sm:mt-6 sm:gap-3">
                  <Link to="/tournaments">
                    <Button className="rounded-full px-5">
                      <Plus className="h-4 w-4" />
                      Create Tournament
                    </Button>
                  </Link>
                  <Link to="/tournaments">
                    <Button variant="outline" className="rounded-full border-white/10 bg-black/20 text-white hover:bg-white/[0.06]">
                      Manage Fixtures
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                <div className="rounded-[22px] border border-white/10 bg-black/25 p-3.5 sm:rounded-3xl sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Live Overview</p>
                  <p className="mt-2 text-3xl font-bold text-white score-display sm:mt-4 sm:text-4xl">{stats?.live_matches || 0}</p>
                  <p className="mt-1 text-[13px] leading-5 text-white/55 sm:mt-2 sm:text-sm">Live fixtures now</p>
                </div>
                <div className="rounded-[22px] border border-white/10 bg-black/25 p-3.5 sm:rounded-3xl sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Alert Window</p>
                  <p className="mt-2 text-3xl font-bold text-white score-display sm:mt-4 sm:text-4xl">{alerts.length}</p>
                  <p className="mt-1 text-[13px] leading-5 text-white/55 sm:mt-2 sm:text-sm">Items to review</p>
                </div>
                <div className="rounded-[22px] border border-white/10 bg-black/25 p-3.5 sm:col-span-2 sm:rounded-3xl sm:p-5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/35 sm:text-[11px] sm:tracking-[0.28em]">Recent Tournaments</p>
                  <div className="mt-3 flex flex-wrap gap-2 sm:mt-4">
                    {(stats?.recent_tournaments || []).length === 0 ? (
                      <span className="text-sm text-white/45">No tournament activity yet</span>
                    ) : (
                      stats.recent_tournaments.slice(0, 4).map((tournament) => (
                        <Link
                          key={tournament.id}
                          to={`/tournaments/${tournament.id}`}
                          className="rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-[13px] text-white/70 transition-colors hover:bg-white/[0.09] sm:py-2 sm:text-sm"
                        >
                          {tournament.name}
                        </Link>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="stats-grid">
          {controlMetrics.map((metric) => (
            <MetricCard key={metric.title} {...metric} data-testid={`stat-card-${metric.title.toLowerCase()}`} />
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.35fr_0.9fr]">
          <div className={`space-y-6 ${liveFirst ? 'order-1' : 'order-2 xl:order-1'}`} id="live-section">
            <SectionHeading
              eyebrow="Live Operations"
              title="Match Command Stream"
              body="Active fixture cards expose score state, batter context, bowler figures, and recent over updates."
            />
            <div className="grid gap-4">
              {liveMatches.length === 0 ? (
                <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
                  <CardContent className="px-6 py-12 text-center text-white/45">
                    No live matches at the moment
                  </CardContent>
                </Card>
              ) : (
                liveMatches.map((match) => (
                  <LiveMatchCard key={match.id} match={match} to={`/scoring/${match.id}`} accent="admin" />
                ))
              )}
            </div>
          </div>

          <div className={`space-y-6 ${liveFirst ? 'order-2' : 'order-1 xl:order-2'}`}>
            <AlertPanel alerts={alerts} />
            <AdminQuickActions tournaments={stats?.recent_tournaments || []} />
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <Sparkles className="h-5 w-5 text-primary" />
                Tournament Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(stats?.recent_tournaments || []).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 px-4 py-10 text-center text-white/45">
                  No tournament activity yet
                </div>
              ) : (
                stats.recent_tournaments.map((tournament) => (
                  <Link
                    key={tournament.id}
                    to={`/tournaments/${tournament.id}`}
                  className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 p-3.5 transition-colors hover:bg-white/[0.05] sm:p-4"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[15px] font-semibold text-white sm:text-base">{tournament.name}</p>
                      <p className="text-[13px] leading-5 text-white/45 sm:text-sm">Open tournament workspace, fixtures, and standings</p>
                    </div>
                    <Badge variant={tournament.status === 'live' ? 'default' : 'secondary'}>
                      {tournament.status}
                    </Badge>
                  </Link>
                ))
              )}
            </CardContent>
          </Card>

          <RecentMatchesCard matches={recentMatches} />
        </div>

        <Card className="rounded-[24px] border-white/10 bg-white/[0.04] sm:rounded-3xl">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <Clock3 className="h-5 w-5 text-primary" />
              Control Center Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
              <p className="text-sm font-semibold text-white">Rapid Operations</p>
              <p className="mt-2 text-[13px] leading-5 text-white/55 sm:text-sm sm:leading-6">
                Keep live scoring updated from the dashboard or jump directly into the scorer screen from any live card.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
              <p className="text-sm font-semibold text-white">Conflict Awareness</p>
              <p className="mt-2 text-[13px] leading-5 text-white/55 sm:text-sm sm:leading-6">
                Alert cards are intentionally subtle but visible, so critical operational issues stand out without overwhelming the workspace.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 p-3.5 sm:p-4">
              <p className="text-sm font-semibold text-white">Responsive Shift</p>
              <p className="mt-2 text-[13px] leading-5 text-white/55 sm:text-sm sm:leading-6">
                On mobile, the live stream stays prominent while navigation collapses into the bottom bar for one-handed movement.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
